import { AuthStorage } from "utils/auth-storage";
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import styles from "./hilyte.module.css";
import { HilyteService } from "apis/Services/HilyteService";
import {
  Button,
  Input,
  message,
  Upload,
  Progress,
  List,
  Card,
  Tag,
  Spin,
  Tooltip,
  Table,
  Popconfirm,
} from "antd";
import {
  InboxOutlined,
  FileOutlined,
  FilePdfOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  DeleteFilled,
  CloseOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import ReviewPDF from "./review-pdf";
import PreviewPDF from "./preview-pdf";
import DividePDF from "./divide-pdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PROMPT_2016, PROMPT_2020 } from "./prompts";

const { Dragger } = Upload;

const Hilyte = () => {
  const { company_id, id } = useParams();
  const [accessLevel, setAccessLevel] = useState(null);
  const [remainingUploads, setRemainingUploads] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [data, setData] = useState({});
  const [requestsData, setRequestsData] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [masterFormat, setMasterFormat] = useState("2020");

  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // New state for managing file list
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [userFiles, setUserFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const [prompt, setPrompt] = useState("");
  const [promptOpen, setPromptOpen] = useState(false);
  const [tableState, setTableState] = useState({});

  // Animation state
  const [showIntro, setShowIntro] = useState(true);
  const [showContent, setShowContent] = useState(false);

  // Store abort controllers for each upload
  const abortControllersRef = useRef({});
  const triggeredExtractionsRef = useRef(new Set());

  const { TextArea } = Input;

  const [showDividePDF, setShowDividePDF] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileCsiFormat, setSelectedFileCsiFormat] =
    useState("PROMPT_2020");

  const [isOverwrite, setIsOverwrite] = useState("0");
  const [isCursorMode, setIsCursorMode] = useState(false);
  const [existingSections, setExistingSections] = useState({});

  useEffect(() => {
    getAccessLevel();

    // Trigger animation sequence
    const introTimer = setTimeout(() => {
      setShowIntro(false);
    }, 2000);

    const contentTimer = setTimeout(() => {
      setShowContent(true);
    }, 2500);

    return () => {
      clearTimeout(introTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  // Fetch user files on mount
  useEffect(() => {
    getAllUsersPDF();
  }, []);

  // Fetch user files when showContent becomes true
  useEffect(() => {
    if (showContent || showPreview) {
      getAllUsersPDF();
    }
  }, [showContent, showPreview]);

  // Set up polling and trigger section extraction for files that haven't started yet
  useEffect(() => {
    userFiles.forEach((file) => {
      if (
        file.sections_status === "Not started" &&
        !triggeredExtractionsRef.current.has(file.pdfID)
      ) {
        triggeredExtractionsRef.current.add(file.pdfID);
        extractSectionsFromPDF(file.pdfID);
      }
    });

    // Check if any file is still processing (uploading or extracting)
    const hasProcessingFiles = userFiles.some(
      (file) =>
        file.uploadStatus !== "Uploaded" ||
        (file.sections_status !== "Processed" &&
          file.sections_status !== "Saved"),
    );

    if (!hasProcessingFiles) {
      return; // No need to poll if all files are done
    }

    // Poll while there are processing files
    const intervalId = setInterval(() => {
      getAllUsersPDF();
    }, 20000); // every 20 seconds

    return () => clearInterval(intervalId);
  }, [userFiles]);

  function getAccessLevel() {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");

    let ReqObj = {
      company: company_id,
      project: id,
      code,
      loginEmail: email,
    };

    HilyteService.hilytePageAccessLevel(ReqObj)
      .then((res) => {
        setAccessLevel(parseInt(res.accessLevel));
        setRemainingUploads(parseInt(res.remainingUploads));
      })
      .catch((err) => console.log(err));
  }

  const getAllUsersPDF = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      setLoadingFiles(true);
      const files = await HilyteService.getAllUsersPDF({
        company: company_id,
        loginEmail,
        code,
      });

      setUserFiles(files || []);
    } catch (err) {
      console.error("Error fetching user files:", err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const extractSectionsFromPDF = (pdfID) => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    HilyteService.extractSectionsFromPDF({
      company: company_id,
      loginEmail,
      code,
      pdfID,
    }).catch((err) => {
      console.error("Error starting section extraction:", err);
    });
  };

  const cancelUpload = (fileId) => {
    // Abort the request if controller exists
    if (abortControllersRef.current[fileId]) {
      abortControllersRef.current[fileId].abort();
      delete abortControllersRef.current[fileId];
    }

    // Remove file from uploading list
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
    message.info("Upload cancelled");
  };

  const uploadPDF = async (file) => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "CSIFormat",
      masterFormat === "2020" ? "PROMPT_2020" : "PROMPT_2016",
    );

    // Add file to uploading list
    const fileId = Date.now() + Math.random();

    // Create abort controller for this upload
    const abortController = new AbortController();
    abortControllersRef.current[fileId] = abortController;

    setUploadingFiles((prev) => [
      ...prev,
      {
        id: fileId,
        name: file.name,
        progress: 0,
        file: file,
        status: "uploading",
      },
    ]);

    try {
      setIsUploading(true);

      const res = await HilyteService.uploadPDF({
        company: company_id,
        loginEmail,
        code,
        formData,
        signal: abortController.signal, // Pass the abort signal
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(progressEvent.progress);

          // Update progress for this specific file
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileId ? { ...f, progress: percent } : f,
            ),
          );
        },
      });

      // Clean up abort controller
      delete abortControllersRef.current[fileId];

      if (res?.ok) {
        const data = await res.json();

        // Remove from uploading list
        setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));

        setUploadedFileId(data.fileId);
        setFile(file);
        message.success(`${file.name} uploaded successfully`);

        // Call extract sections using the uploaded fileId from backend
        extractSectionsFromPDF(data.fileID);

        // Refresh the file list
        getAllUsersPDF();
      } else {
        // Remove from uploading list on error
        setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
        message.error(`Failed to upload ${file.name}`);
      }
    } catch (err) {
      // Clean up abort controller
      delete abortControllersRef.current[fileId];

      // Remove from uploading list on error
      setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));

      // Only show error if not aborted
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        console.error(err);
        message.error(`Error uploading ${file.name}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const extractSubmittalsFromPDF = async () => {
    if (!uploadedFileId) {
      message.warning("Please upload a file first");
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    const formData = new FormData();
    formData.append("file", file);

    const resolvedPrompt =
      prompt.trim() || (masterFormat === "2020" ? PROMPT_2020 : PROMPT_2016);

    formData.append("prompt", resolvedPrompt);

    formData.append("images", "");
    formData.append("text_input", "");
    formData.append("fileId", uploadedFileId);

    const loadingMessage = message.loading("Analyzing PDF...", 0);
    setShowPreview(true);

    try {
      let res;
      if (file) {
        res = await HilyteService.extractSubmittalsFromPDF({
          company: company_id,
          loginEmail,
          code,
          formData,
        });
      }

      loadingMessage();

      if (res.ok) {
        const data = await res.json();

        console.log("Upload successful!");
        setData(data?.raw);

        message.success("PDF analyzed successfully!");
      } else {
        message.error("Failed to analyze PDF. Please try again.");
      }
    } catch (err) {
      loadingMessage();

      console.error("Error uploading file:", err);
      message.error("An error occurred while analyzing the PDF.");
    }
  };

  const props = {
    name: "file",
    multiple: true,
    accept: ".pdf",
    customRequest: ({ file, onSuccess }) => {
      uploadPDF(file);
      onSuccess("ok");
    },
    showUploadList: false,
  };

  const handleOk = () => {
    setIsReviewModalOpen(false);
    setData({});
  };
  const handleCancel = () => {
    setIsReviewModalOpen(false);
    setData({});
  };

  const exportCSV = (data) => {
    if (!data || typeof data !== "object") return;

    const rows = [];

    Object.entries(data).forEach(([divisionKey, subdivisions]) => {
      Object.entries(subdivisions || {}).forEach(
        ([subdivisionName, submittals]) => {
          (submittals || []).forEach((item) => {
            if (item.isPending) return;

            rows.push({
              "Spec Section - Spec Section Name": `${divisionKey} - ${subdivisionName}`,
              "Submittal Number": item.specNumber || "",
              Title: item.specTitle || "",
              Revision: item.specRevision || "",
              Type: item.specType || "",
              Response: item.response || "",
              "Responsible Contractor": item.responsibleContractor || "",
              "Issue Contract": item.issueContract || "",
              "Contract Issued Release": item.contractIssuedRelease || "",
              "Submittal Prep Time": item.submittalPrepTime || "",
              "Submittal Required": item.submittalRequired || "",
              "Submittal Issued": item.submittalIssued || "",
              "Approval Time": item.approvalTime || "",
              "Submittal Approval Due": item.submittalApprovalDue || "",
              "Submittal Returned": item.submittalReturned || "",
              "Lead Time": item.leadTime || "",
              "Schedule ID": item.scheduleId || "",
              "Date Required On Site": item.dateRequiredOnSite || "",
              "Projected Delivery Date": item.projectedDeliveryDate || "",
              "Sub Committed Delivery Date":
                item.subCommittedDeliveryDate || "",
              "Actual Delivery Date": item.actualDeliveryDate || "",
            });
          });
        },
      );
    });

    if (rows.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Approved Submittals");

    const csv = XLSX.write(workbook, {
      bookType: "csv",
      type: "array",
    });

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, "approved-submittals.csv");
  };

  const hasApprovedSubmittals = Object.values(tableState || {}).some(
    (subdivisions) =>
      Object.values(subdivisions || {}).some((items) =>
        items?.some((item) => !item.isPending),
      ),
  );

  const getStatusTag = (uploadStatus) => {
    if (uploadStatus === "Uploaded") {
      return (
        <Tooltip title={uploadStatus}>
          <Tag icon={<CheckCircleOutlined />} color="success">
            {/* Uploaded */}
          </Tag>
        </Tooltip>
      );
    } else if (uploadStatus === "Uploading") {
      return (
        <Tooltip title={uploadStatus}>
          <Tag
            className=" d-flex justify-content-center align-items-center"
            icon={<SyncOutlined spin />}
            color="processing"
          >
            {/* {uploadStatus} */}
          </Tag>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={uploadStatus}>
          <Tag
            className=" d-flex justify-content-center align-items-center"
            icon={<CloseCircleOutlined />}
            color="error"
          >
            {/* {uploadStatus} */}
          </Tag>
        </Tooltip>
      );
    }
  };

  function deletePDF(pdfID) {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");

    let ReqObj = {
      company: company_id,
      project: id,
      code,
      loginEmail: email,
      pdfID,
    };

    HilyteService.deletePDF(ReqObj)
      .then((res) => {
        message.success("File deleted successfully");
        getAllUsersPDF();
      })
      .catch((err) => {
        console.log(err);
        message.error("Failed to delete file");
      });
  }

  return (
    <div className={styles.container}>
      {showDividePDF ? (
        <div className={styles.fadeIn}>
          <div
            className={`${styles.hero} ${!showIntro ? styles.heroAnimated : styles.heroHidden}`}
          >
            <h1 className={styles.titleGradient}>Hi-LYTE</h1>
          </div>
          <DividePDF
            pdfID={selectedFileId}
            onBack={() => {
              setShowDividePDF(false);
              setSelectedFileId(null);
            }}
            setShowPreview={setShowPreview}
            setShowDividePDF={setShowDividePDF}
            setFileUrl={setFileUrl}
          />
        </div>
      ) : (
        <>
          {/* INTRO ANIMATION */}
          {showIntro && (
            <div className={styles.introOverlay}>
              <h1 className={styles.introTitle}>Hi-LYTE</h1>
            </div>
          )}

          {/* HERO SECTION */}
          <div
            className={`${styles.hero} ${!showIntro ? styles.heroAnimated : styles.heroHidden}`}
          >
            <h1 className={styles.titleGradient}>Hi-LYTE</h1>
            {showContent && !showPreview && (
              <p className={`${styles.subtitle} text-muted ${styles.fadeIn}`}>
                Powered by AI — upload any PDF and let Hi-Lyte find all
                submittals, extract key details, and import them directly into
                your Procurement Log, saving you hours of manual entry.
              </p>
            )}
          </div>

          {!showPreview && showContent && (
            <div className={styles.fadeIn}>
              <div className="mt-3">
                <div className="my-2 text-muted text-center">
                  MasterFormat Version
                </div>

                <div className="d-flex gap-2 mb-5 justify-content-center">
                  <Button
                    size="large"
                    shape="round"
                    type={masterFormat === "2016" ? "primary" : "default"}
                    onClick={() => setMasterFormat("2016")}
                  >
                    CSI MasterFormat 2016
                  </Button>

                  <Button
                    size="large"
                    shape="round"
                    type={masterFormat === "2020" ? "primary" : "default"}
                    onClick={() => setMasterFormat("2020")}
                  >
                    CSI MasterFormat 2020
                  </Button>
                </div>
              </div>

              <div className=" d-flex justify-content-center align-items-center w-100">
                <div
                  className={`${styles.uploadBox}`}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div style={{ width: "100%", maxWidth: 700 }}>
                    {/* Collapsible Custom Prompt */}
                    <div style={{ marginBottom: 12 }}>
                      <div
                        onClick={() => setPromptOpen((o) => !o)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          padding: "6px 12px",
                          borderRadius: 8,
                          background: promptOpen ? "#e6f4ff" : "#f5f5f5",
                          border: `1px solid ${promptOpen ? "#91caff" : "#d9d9d9"}`,
                          transition: "all 0.2s",
                          userSelect: "none",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: promptOpen ? "#0958d9" : "#595959",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Prompt
                          {prompt.trim() && (
                            <Tag
                              color="blue"
                              style={{ fontSize: 11, marginLeft: 4 }}
                            >
                              Active
                            </Tag>
                          )}
                        </span>
                        <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                          {promptOpen ? "▲ Hide" : "▼ Expand"}
                        </span>
                      </div>

                      {promptOpen && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 12,
                            background: "#fafafa",
                            border: "1px solid #e0e0e0",
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              color: "#8c8c8c",
                              marginBottom: 8,
                            }}
                          >
                            Override the default prompt used to analyze the PDF.
                            {masterFormat === "2020"
                              ? "MasterFormat 2020"
                              : "MasterFormat 2016"}{" "}
                            prompt.
                          </div>
                          <TextArea
                            rows={6}
                            placeholder={`Leave blank to use the default ${masterFormat === "2020" ? "MasterFormat 2020" : "MasterFormat 2016"} prompt...`}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            style={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              resize: "vertical",
                            }}
                          />
                          {prompt.trim() && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                size="small"
                                danger
                                onClick={() => setPrompt("")}
                              >
                                Clear prompt
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <Dragger {...props}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined
                          style={{ fontSize: 40, color: "#0AA6E9" }}
                        />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag your PDF file here
                      </p>
                      <p className="ant-upload-hint">
                        Only PDF files are supported. Your data stays private
                        and secure.
                      </p>
                    </Dragger>
                    {/* File Upload Progress List */}
                    {uploadingFiles.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <List
                          size="small"
                          dataSource={uploadingFiles}
                          renderItem={(item) => (
                            <List.Item>
                              <div style={{ width: "100%" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <FileOutlined
                                      style={{
                                        marginRight: 8,
                                        color: "#0AA6E9",
                                      }}
                                    />
                                    <span>{item.name}</span>
                                  </div>
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={() => cancelUpload(item.id)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                                <Progress
                                  percent={item.progress}
                                  status="active"
                                />
                              </div>
                            </List.Item>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* User Files Table */}
              <div style={{ marginTop: 24 }}>
                <Card
                  title={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <FilePdfOutlined style={{ color: "#0AA6E9" }} />
                      <span>Your Uploaded Files</span>
                      {loadingFiles && <Spin size="small" />}
                    </div>
                  }
                  bordered={false}
                >
                  {userFiles.length === 0 && !loadingFiles ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#999",
                      }}
                    >
                      No files uploaded yet
                    </div>
                  ) : (
                    <Table
                      dataSource={[...userFiles].reverse()}
                      rowKey={(record) => record.pdfID}
                      pagination={false}
                      showHeader={false}
                      scroll={{ y: 400 }}
                      style={{ width: 700 }}
                      columns={[
                        {
                          title: "File",
                          dataIndex: "fileName",
                          key: "fileName",
                          render: (text, record) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <FilePdfOutlined
                                style={{ fontSize: 20, color: "#ff4d4f" }}
                              />
                              <span>{text}</span>
                            </div>
                          ),
                        },
                        {
                          title: "Cloud",
                          width: 100,
                          key: "cloud",
                          render: (_, record) => (
                            <div className=" d-flex gap-1">
                              {getStatusTag(record.uploadStatus)}
                              <Tooltip title={record.s3_status}>
                                <Tag
                                  icon={<CloudOutlined />}
                                  color={
                                    record.s3_status === "Available"
                                      ? "blue"
                                      : "Missing"
                                        ? "default"
                                        : "error"
                                  }
                                />
                              </Tooltip>
                            </div>
                          ),
                        },
                        {
                          title: "Format",
                          key: "csiFormat",
                          width: 85,
                          render: (_, record) => {
                            const fmt =
                              record.CSIFormat === "PROMPT_2016"
                                ? "2016"
                                : "2020";
                            return (
                              <Tag color={fmt === "2020" ? "blue" : "purple"}>
                                MF {fmt}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: "Action",
                          key: "action",
                          width: 250,
                          render: (_, record) => (
                            <div className=" d-flex gap-2 justify-content-center align-items-center">
                              <Button
                                type="primary"
                                shape="round"
                                disabled={
                                  record.uploadStatus !== "Uploaded" ||
                                  record.s3_status !== "Available" ||
                                  (record.sections_status !== "Processed" &&
                                    record.sections_status !== "Saved")
                                }
                                onClick={() => {
                                  setSelectedFileId(record.pdfID);
                                  setSelectedFileCsiFormat(
                                    record.CSIFormat || "PROMPT_2020",
                                  );
                                  setShowDividePDF(true);
                                }}
                              >
                                {record.sections_status !== "Processed" &&
                                record.sections_status !== "Saved"
                                  ? "Processing"
                                  : "Review"}
                              </Button>
                              <Button
                                type="default"
                                shape="round"
                                disabled={
                                  record.sections_status !== "Saved" ||
                                  record.uploadStatus !== "Uploaded" ||
                                  record.s3_status !== "Available"
                                }
                                onClick={() => {
                                  setSelectedFileId(record.pdfID);
                                  setSelectedFileCsiFormat(
                                    record.CSIFormat || "PROMPT_2020",
                                  );
                                  setData({}); // clear previous analysis data
                                  setRequestsData({}); // clear previous requests data
                                  setShowPreview(true);
                                }}
                              >
                                Analyze
                              </Button>
                              <Popconfirm
                                title="Delete file?"
                                description="Are you sure you want to delete this PDF?"
                                okText="Delete"
                                cancelText="No"
                                onConfirm={() => deletePDF(record.pdfID)}
                              >
                                <Button type="text" size="small" danger>
                                  <DeleteOutlined />
                                </Button>
                              </Popconfirm>
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}
                </Card>
              </div>
            </div>
          )}
          {showPreview && (
            <div className="d-flex flex-column gap-4">
              <PreviewPDF
                pdfID={selectedFileId}
                data={data}
                file={fileUrl}
                PDF={file}
                setData={setData}
                setRequestsData={setRequestsData}
                findSubmittalsPrompt={
                  prompt.trim() ||
                  (masterFormat === "2016" ? PROMPT_2016 : PROMPT_2020)
                }
                onBack={() => setShowPreview(false)}
                setFileUrl={setFileUrl}
                setIsOverwrite={setIsOverwrite}
                setIsCursorMode={setIsCursorMode}
                setExistingSections={setExistingSections}
                fileCsiFormat={selectedFileCsiFormat}
                tableState={tableState}
              />

              <div
                className="bg-white shadow-sm rounded p-2"
                style={{
                  width: "95vw",
                  maxWidth: "2040px",
                  margin: "0 auto",
                }}
              >
                <div className="d-flex justify-content-between pt-2">
                  <div className="d-flex gap-2"></div>

                  <Button
                    size="large"
                    disabled={!hasApprovedSubmittals}
                    onClick={() => {
                      exportCSV(tableState);
                    }}
                  >
                    Export CSV
                  </Button>
                </div>
                <hr className="w-100" />
                <ReviewPDF
                  data={data}
                  requestsData={requestsData}
                  setRequestsData={setRequestsData}
                  tableState={tableState}
                  masterFormat={
                    selectedFileCsiFormat === "PROMPT_2016" ? "2016" : "2020"
                  }
                  setTableState={setTableState}
                  isOverwrite={isOverwrite}
                  isCursorMode={isCursorMode}
                  pdfID={selectedFileId}
                  existingSections={existingSections}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Hilyte;
